/**
 * Decides when to poll. It only keeps time: what a poll does (and what counts as "new") belongs to the caller.
 *
 * - Polls at once on start, then after `baseSeconds`. The first poll after a start or resume never backs off.
 * - Each later poll that finds nothing doubles the wait, up to `maxSeconds`; a poll that finds something resets it.
 * - Pauses while the tab is hidden. Becoming visible or gaining window focus polls at once and resets the wait.
 * - Never overlaps two polls.
 * - A poll that throws counts as finding nothing. A poll that returns `'stop'` (a 401, say) ends polling.
 */
export type PollOutcome = 'new' | 'idle' | 'stop'

export interface PollSchedulerOptions {
  poll: () => Promise<PollOutcome>
  /** Read each time, so a server-provided interval can change while running. */
  baseSeconds: () => number
  maxSeconds: () => number
  isVisible?: () => boolean
  events?: { document?: EventTarget; window?: EventTarget }
}

export interface PollScheduler {
  start: () => void
  stop: () => void
  /** Poll now unless one is already running. */
  pollNow: () => void
  readonly isRunning: boolean
  /** The wait before the next scheduled poll. */
  readonly delaySeconds: number
}

export function createPollScheduler(options: PollSchedulerOptions): PollScheduler {
  const isVisible = options.isVisible ?? (() => document.visibilityState === 'visible')
  const doc = options.events?.document ?? (typeof document === 'undefined' ? undefined : document)
  const win = options.events?.window ?? (typeof window === 'undefined' ? undefined : window)

  let timer: ReturnType<typeof setTimeout> | null = null
  let running = false
  let inFlight = false
  let delay = options.baseSeconds()

  function clearTimer() {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  function schedule() {
    clearTimer()
    if (!running || !isVisible()) return
    timer = setTimeout(() => void tick(), delay * 1000)
  }

  async function tick(isFirstOfRun = false) {
    timer = null
    if (!running || inFlight) return

    inFlight = true
    let outcome: PollOutcome
    try {
      outcome = await options.poll()
    } catch {
      outcome = 'idle'
    } finally {
      inFlight = false
    }

    if (!running) return
    if (outcome === 'stop') {
      stop()
      return
    }

    delay =
      outcome === 'new' || isFirstOfRun
        ? options.baseSeconds()
        : Math.min(delay * 2, options.maxSeconds())
    schedule()
  }

  function resume() {
    if (!running || !isVisible()) return
    delay = options.baseSeconds()
    clearTimer()
    void tick(true)
  }

  function onVisibilityChange() {
    if (isVisible()) resume()
    else clearTimer()
  }

  function start() {
    if (running) return
    running = true
    delay = options.baseSeconds()
    doc?.addEventListener('visibilitychange', onVisibilityChange)
    win?.addEventListener('focus', resume)
    if (isVisible()) void tick(true)
  }

  function stop() {
    running = false
    clearTimer()
    doc?.removeEventListener('visibilitychange', onVisibilityChange)
    win?.removeEventListener('focus', resume)
  }

  function pollNow() {
    if (!running) return
    clearTimer()
    void tick(true)
  }

  return {
    start,
    stop,
    pollNow,
    get isRunning() {
      return running
    },
    get delaySeconds() {
      return delay
    }
  }
}
